// 3D Torus Layout Engine
// Provides 3D cubical layout with perspective projection for torus topologies

import { VisualizationNode } from '../types/visualization';

interface Coordinates3D {
  x: number;
  y: number;
  z: number;
}

interface TorusDetectionResult {
  isTorus: boolean;
  dimensions?: {
    x: number;
    y: number;
    z: number;
  };
  nodes?: Map<string, Coordinates3D>;
}

/**
 * Parse coordinate-based node names to extract 3D position
 * Supports formats like:
 * - cube01-x0-y1-z2
 * - cube01x0y1z2
 * - node-x0-y1-z2
 */
function parseCoordinates(nodeName: string): Coordinates3D | null {
  // Pattern 1: cube01-x0-y1-z2 (with dashes)
  const pattern1 = /x(\d+)-y(\d+)-z(\d+)/;
  const match1 = nodeName.match(pattern1);
  if (match1) {
    return {
      x: parseInt(match1[1], 10),
      y: parseInt(match1[2], 10),
      z: parseInt(match1[3], 10)
    };
  }

  // Pattern 2: cube01x0y1z2 (without dashes)
  const pattern2 = /x(\d+)y(\d+)z(\d+)/;
  const match2 = nodeName.match(pattern2);
  if (match2) {
    return {
      x: parseInt(match2[1], 10),
      y: parseInt(match2[2], 10),
      z: parseInt(match2[3], 10)
    };
  }

  return null;
}

/**
 * Detect if nodes form a 3D torus/cube topology
 */
export function detectTorusTopology(nodes: VisualizationNode[]): TorusDetectionResult {
  // Only consider device-type nodes for torus detection
  // Components, racks, pods, etc. should not trigger torus layout
  const deviceNodes = nodes.filter(node => node.type === 'device');

  if (deviceNodes.length < 8) {
    return { isTorus: false };
  }

  const coordinateMap = new Map<string, Coordinates3D>();
  const uniquePositions = new Set<string>();
  let maxX = -1;
  let maxY = -1;
  let maxZ = -1;
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;

  // Parse coordinates from device node names only
  for (const node of deviceNodes) {
    const coords = parseCoordinates(node.id);
    if (coords) {
      coordinateMap.set(node.id, coords);
      // Track unique positions to detect duplicates
      const posKey = `${coords.x},${coords.y},${coords.z}`;
      uniquePositions.add(posKey);
      maxX = Math.max(maxX, coords.x);
      maxY = Math.max(maxY, coords.y);
      maxZ = Math.max(maxZ, coords.z);
      minX = Math.min(minX, coords.x);
      minY = Math.min(minY, coords.y);
      minZ = Math.min(minZ, coords.z);
    }
  }

  // Need at least 8 nodes (2x2x2) with parsed coordinates
  if (coordinateMap.size < 8) {
    return { isTorus: false };
  }

  // Check if we have at least 8 unique coordinate positions
  // If all devices share the same coordinates, this is not a torus
  if (uniquePositions.size < 8) {
    return { isTorus: false };
  }

  // Check if we have a regular grid
  const dimX = maxX - minX + 1;
  const dimY = maxY - minY + 1;
  const dimZ = maxZ - minZ + 1;

  // All three dimensions should be at least 2
  if (dimX < 2 || dimY < 2 || dimZ < 2) {
    return { isTorus: false };
  }

  // Should have nodes for most of the grid positions
  const expectedNodes = dimX * dimY * dimZ;
  const actualUniqueNodes = uniquePositions.size;

  // Allow some tolerance (e.g., 80% coverage)
  if (actualUniqueNodes < expectedNodes * 0.8) {
    return { isTorus: false };
  }

  return {
    isTorus: true,
    dimensions: { x: dimX, y: dimY, z: dimZ },
    nodes: coordinateMap
  };
}

/**
 * Project 3D coordinates to 2D with perspective
 */
function project3Dto2D(
  x: number,
  y: number,
  z: number,
  viewDistance: number = 1000,
  scale: number = 100
): { x: number; y: number } {
  // Apply isometric-like perspective
  // Z axis goes "into" the screen, creating depth
  const perspective = viewDistance / (viewDistance + z * scale);

  // Isometric projection with perspective
  const screenX = (x - y) * 0.866 * scale * perspective;
  const screenY = (x + y) * 0.5 * scale * perspective - z * scale * perspective;

  return { x: screenX, y: screenY };
}

/**
 * Apply 3D torus layout to nodes
 */
export function applyTorusLayout(
  nodes: VisualizationNode[],
  width: number,
  height: number
): VisualizationNode[] {
  const detection = detectTorusTopology(nodes);

  if (!detection.isTorus || !detection.dimensions || !detection.nodes) {
    // Not a torus topology, return unchanged
    return nodes;
  }

  const { x: dimX, y: dimY, z: dimZ } = detection.dimensions;
  const coordinateMap = detection.nodes;

  // Calculate scale to fit in viewport
  const maxDim = Math.max(dimX, dimY, dimZ);
  const scale = Math.min(width, height) / (maxDim * 3);

  // Calculate center offset
  const centerX = width / 2;
  const centerY = height / 2;

  // Apply 3D layout
  return nodes.map(node => {
    const coords = coordinateMap.get(node.id);
    if (!coords) {
      // Node doesn't have coordinates, keep original position
      return node;
    }

    // Normalize coordinates to center around origin
    const normalizedX = coords.x - (dimX - 1) / 2;
    const normalizedY = coords.y - (dimY - 1) / 2;
    const normalizedZ = coords.z - (dimZ - 1) / 2;

    // Project to 2D with perspective
    const projected = project3Dto2D(
      normalizedX,
      normalizedY,
      normalizedZ,
      1000,
      scale
    );

    return {
      ...node,
      x: centerX + projected.x,
      y: centerY + projected.y,
      // Store original 3D coordinates for future use
      metadata: {
        ...node.metadata,
        coordinates3d: coords,
        depth: normalizedZ // For z-ordering
      }
    };
  });
}

/**
 * Check if torus layout should be applied
 */
export function shouldUseTorusLayout(nodes: VisualizationNode[]): boolean {
  const detection = detectTorusTopology(nodes);
  return detection.isTorus;
}
